/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/**
 * Helper hooks for accessing specific module settings
 */

import type {
  AIExplorerSettings,
  AITranslatorSettings,
  FavoriteSessions,
  UISettings,
  UserSettings,
  VideoInsightSettings,
} from "@/types/user-settings"

import { useSerializedGeneralSettingsUpdate } from "./use-serialized-general-settings-update"
import { useUpdateSettingsSection, useUserSettings } from "./use-user-settings"

/**
 * Hook for AI Explorer settings with per-variant storage support.
 *
 * Variants use naming convention: ai_explorer_{sessionType}
 * (e.g., ai_explorer_variant_a, ai_explorer_variant_b)
 *
 * @param sessionType - Optional session type identifier for variant-specific settings
 * @returns Settings object, loading state, and update function
 *
 * @remarks
 * When variant-specific settings don't exist (undefined), the updateSettings function
 * will gracefully initialize them with an empty object as the base, merging in any
 * provided updates. This allows variants to start with default/empty settings and
 * build up their configuration over time without requiring pre-initialization.
 */
export function useAIExplorerSettings(sessionType?: string) {
  const { data: userSettings, isLoading } = useUserSettings() as {
    data: UserSettings | undefined
    isLoading: boolean
  }
  const { mutate: updateSection } = useUpdateSettingsSection()

  // Use ai_explorer_{sessionType} for variants, "ai_explorer" for legacy/default
  const sectionName = sessionType ? `ai_explorer_${sessionType}` : "ai_explorer"
  const variantSettings = userSettings?.data?.[sectionName as keyof typeof userSettings.data] as
    | AIExplorerSettings
    | undefined

  const updateAIExplorerSettings = (data: Partial<AIExplorerSettings>) => {
    updateSection({
      section: sectionName,
      data: {
        ...(variantSettings ?? {}),
        ...data,
      },
    })
  }

  return {
    settings: variantSettings,
    isLoading,
    updateSettings: updateAIExplorerSettings,
  }
}

/**
 * Hook for AI Translator settings
 * Note: For AI Translator, use useAITranslatorSettingsLocal for debounced updates
 */
export function useAITranslatorSettings() {
  const { data: userSettings, isLoading } = useUserSettings() as {
    data: UserSettings | undefined
    isLoading: boolean
  }
  const { mutate: updateSection } = useUpdateSettingsSection()

  const aiTranslatorSettings = userSettings?.data?.ai_translator

  const updateAITranslatorSettings = (data: Partial<AITranslatorSettings>) => {
    updateSection({
      section: "ai_translator",
      data: {
        ...(aiTranslatorSettings ?? {}),
        ...data,
      },
    })
  }

  return {
    settings: aiTranslatorSettings,
    isLoading,
    updateSettings: updateAITranslatorSettings,
  }
}

/**
 * Hook for Video Insight settings
 */
export function useVideoInsightSettings() {
  const { data: userSettings, isLoading } = useUserSettings() as {
    data: UserSettings | undefined
    isLoading: boolean
  }
  const { mutate: updateSection } = useUpdateSettingsSection()

  const videoInsightSettings = userSettings?.data?.video_insight

  const updateVideoInsightSettings = (data: Partial<VideoInsightSettings>) => {
    updateSection({
      section: "video_insight",
      data: {
        ...(videoInsightSettings ?? {}),
        ...data,
      },
    })
  }

  return {
    settings: videoInsightSettings,
    isLoading,
    updateSettings: updateVideoInsightSettings,
  }
}

/**
 * Hook for general app settings
 */
export function useGeneralSettings() {
  const { data: userSettings, isLoading } = useUserSettings() as {
    data: UserSettings | undefined
    isLoading: boolean
  }
  const updateGeneralSettings = useSerializedGeneralSettingsUpdate()

  const generalSettings = userSettings?.data?.settings

  return {
    settings: generalSettings,
    isLoading,
    updateSettings: updateGeneralSettings,
  }
}

/**
 * Hook for UI settings (GDPR, What's New, etc.)
 */
export function useUISettings() {
  const {
    data: userSettings,
    isLoading,
    isFetching,
  } = useUserSettings() as {
    data: UserSettings | undefined
    isLoading: boolean
    isFetching: boolean
  }
  const { mutate: updateSection } = useUpdateSettingsSection()

  const uiSettings = userSettings?.data?.ui

  const updateUISettings = (
    data: Partial<UISettings>,
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => {
    updateSection(
      {
        section: "ui",
        data: {
          ...(uiSettings ?? {}),
          ...data,
        },
      },
      {
        onSuccess: options?.onSuccess,
        onError: options?.onError,
      }
    )
  }

  return {
    settings: uiSettings,
    isLoading,
    isFetching,
    updateSettings: updateUISettings,
  }
}

/**
 * Hook for favorite sessions
 */
export function useFavoriteSessions() {
  const { data: userSettings, isLoading } = useUserSettings() as {
    data: UserSettings | undefined
    isLoading: boolean
  }
  const { mutate: updateSection } = useUpdateSettingsSection()

  const favoriteSessions = userSettings?.data?.favorite_sessions

  const updateFavoriteSessions = (data: Partial<FavoriteSessions>) => {
    updateSection({
      section: "favorite_sessions",
      data: {
        ...(favoriteSessions ?? {}),
        ...data,
      },
    })
  }

  return {
    favorites: favoriteSessions,
    isLoading,
    updateFavorites: updateFavoriteSessions,
  }
}
